import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCheck, LuChevronDown, LuSearch, LuX } from 'react-icons/lu';

const MENU_MAX = 320;

/**
 * Searchable entity switcher (command-style).
 *
 * Modes:
 * - Local filter (default): filters `options` client-side.
 * - Remote: pass `onSearchChange` — parent refetches; list shows `options` as-is.
 * - Pagination: pass `hasMore` + `onLoadMore` for scroll-to-load-more.
 */
const EntitySearchSelect = ({
  id,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  disabled = false,
  className = '',
  'aria-label': ariaLabel,
  onSearchChange,
  hasMore = false,
  loadingMore = false,
  searching = false,
  onLoadMore,
  selectedOption = null,
  footerHint = null,
}) => {
  const autoId = useId();
  const baseId = id || `entity-ss-${autoId.replace(/:/g, '')}`;
  const listId = `${baseId}-list`;
  const searchId = `${baseId}-search`;
  const remote = typeof onSearchChange === 'function';

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [coords, setCoords] = useState(null);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const listScrollRef = useRef(null);
  const searchRef = useRef(null);

  const current = value == null ? '' : String(value);
  const selected = useMemo(() => {
    if (selectedOption && String(selectedOption.value) === current) return selectedOption;
    return options.find((o) => String(o.value) === current) || null;
  }, [options, current, selectedOption]);

  const filtered = useMemo(() => {
    if (remote) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label || ''} ${o.meta || ''} ${o.value || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query, remote]);

  const measure = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const up = below < Math.min(MENU_MAX, 280) + 12 && rect.top > below;
    const width = Math.max(rect.width, 280);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setCoords({
      left,
      width,
      top: up ? 'auto' : rect.bottom + 4,
      bottom: up ? window.innerHeight - rect.top + 4 : 'auto',
      maxHeight: Math.max(160, Math.min(MENU_MAX, (up ? rect.top : below) - 16)),
      placeUp: up,
    });
  }, []);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    if (remote) onSearchChange?.('');
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  }, [remote, onSearchChange]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    measure();
    const idx = Math.max(
      0,
      filtered.findIndex((o) => String(o.value) === current)
    );
    setActiveIndex(idx >= 0 ? idx : 0);
    setOpen(true);
  }, [disabled, measure, filtered, current]);

  const commit = useCallback(
    (opt) => {
      if (!opt) return;
      if (String(opt.value) !== current) onChange?.(opt.value);
      close();
    },
    [current, onChange, close]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      close(false);
    };
    const onReflow = () => measure();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, close, measure]);

  useLayoutEffect(() => {
    if (!open) return;
    searchRef.current?.focus({ preventScroll: true });
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !listScrollRef.current) return;
    const el = listScrollRef.current.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, filtered]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, options]);

  const handleQueryChange = (next) => {
    setQuery(next);
    if (remote) onSearchChange?.(next);
  };

  const handleListScroll = (e) => {
    if (!hasMore || loadingMore || typeof onLoadMore !== 'function') return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      onLoadMore();
    }
  };

  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) openMenu();
    }
  };

  const onSearchKeyDown = (e) => {
    const n = filtered.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (n) setActiveIndex((i) => (i + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (n) setActiveIndex((i) => (i - 1 + n) % n);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        if (n) setActiveIndex(n - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (filtered[activeIndex]) commit(filtered[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  };

  const display = selected?.label || placeholder;
  const rootClass = ['rt-entity-ss', open ? 'is-open' : '', disabled ? 'is-disabled' : '', className]
    .filter(Boolean)
    .join(' ');

  const menu = coords && (
    <div
      ref={listRef}
      id={listId}
      className={`rt-entity-ss__menu ${coords.placeUp ? 'is-up' : ''}`}
      role="listbox"
      aria-labelledby={baseId}
      data-rt-modal-keep-escape=""
      style={{
        position: 'fixed',
        left: coords.left,
        width: coords.width,
        minWidth: coords.width,
        maxWidth: Math.min(window.innerWidth - 16, 560),
        top: coords.top,
        bottom: coords.bottom,
        maxHeight: coords.maxHeight,
        zIndex: 20000,
      }}
    >
      <div className="rt-entity-ss__search">
        <LuSearch size={14} aria-hidden />
        <input
          ref={searchRef}
          id={searchId}
          type="search"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          autoComplete="off"
        />
        {query ? (
          <button
            type="button"
            className="rt-entity-ss__clear"
            onClick={() => {
              handleQueryChange('');
              searchRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <LuX size={14} />
          </button>
        ) : null}
      </div>
      <ul
        ref={listScrollRef}
        className="rt-entity-ss__list"
        role="presentation"
        onScroll={handleListScroll}
      >
        {searching && filtered.length === 0 ? (
          <li className="rt-entity-ss__empty">Searching…</li>
        ) : filtered.length === 0 ? (
          <li className="rt-entity-ss__empty">No matches</li>
        ) : (
          filtered.map((opt, index) => {
            const isSelected = String(opt.value) === current;
            const isActive = index === activeIndex;
            return (
              <li
                key={opt.value}
                id={`${baseId}-opt-${index}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                className={[
                  'rt-entity-ss__option',
                  isActive ? 'is-active' : '',
                  isSelected ? 'is-selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(opt)}
              >
                <span className="rt-entity-ss__option-main">
                  <span className="rt-entity-ss__option-label">{opt.label}</span>
                  {opt.meta ? <span className="rt-entity-ss__option-meta">{opt.meta}</span> : null}
                </span>
                {isSelected ? <LuCheck size={15} aria-hidden /> : null}
              </li>
            );
          })
        )}
        {loadingMore ? <li className="rt-entity-ss__empty">Loading more…</li> : null}
        {!loadingMore && hasMore ? (
          <li className="rt-entity-ss__hint">Scroll for more</li>
        ) : null}
      </ul>
      {footerHint ? <div className="rt-entity-ss__footer">{footerHint}</div> : null}
    </div>
  );

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        className="rt-entity-ss__trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={
          open && filtered[activeIndex] ? `${baseId}-opt-${activeIndex}` : undefined
        }
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`rt-entity-ss__value ${!selected ? 'is-placeholder' : ''}`}>{display}</span>
        <LuChevronDown className="rt-entity-ss__chevron" size={16} aria-hidden />
      </button>
      {open && createPortal(menu, document.body)}
    </div>
  );
};

export default EntitySearchSelect;
